import { useEffect, useState } from "react";
import { CARD, CARD2, BD, TX, TX2, GR, BL, YL, RD, PU } from "../../utils/constants";
import { scoreColor, gradeLabel, biasColor, fmt } from "../../utils/formatUtils";

/**
 * One scoring pillar.
 *
 * `evaluated={false}` draws the row greyed out with "not evaluated" instead of
 * a number. That case is not cosmetic: when fundamentals are unavailable the
 * pillar keeps its neutral starting value of 50 and its weight is redistributed
 * to the technicals, so the 50 contributed nothing to the score. Rendering it
 * as a half-full bar showed an input that had no influence as though it were a
 * real, middling result — the same "missing data must never look like a
 * legitimate value" rule the rest of the app follows.
 */
function SubBar({ label, value, color, evaluated = true }) {
  const shown = evaluated && Number.isFinite(value);
  return (
    <div style={{ marginBottom: 5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 8, color: TX2, letterSpacing: 1 }}>{label}</span>
        <span style={{ fontSize: 8, fontWeight: 700, color: shown ? color : TX2 }}>
          {shown ? value : "not evaluated"}
        </span>
      </div>
      <div style={{ height: 3, background: CARD2, borderRadius: 2 }}>
        {shown && (
          <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 2, transition: "width 0.4s" }} />
        )}
      </div>
    </div>
  );
}

function useCountUp(target, duration = 600) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

export default function StockCard({ stock, rank, isWatchlisted, onToggleWL, onStockClick }) {
  const rr = stock.riskReward;
  const displayScore = useCountUp(stock.score, 500);

  return (
    <div className="card-h ts-panel" onClick={() => onStockClick(stock)}
      style={{ background: CARD, border: `1px solid ${rank === 1 ? GR + "35" : BD}`, borderLeft: `3px solid ${scoreColor(stock.score)}`, borderRadius: 10, padding: "11px 14px", marginBottom: 7, boxShadow: rank === 1 ? `0 0 20px ${GR}06` : "none" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>

        <div style={{ minWidth: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 9, color: rank === 1 ? GR : TX2, fontWeight: 700 }}>#{rank}</span>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif", color: TX }}>{stock.symbol}</span>
            {stock.isFMP     && <span style={{ fontSize: 7, background: BL  + "20", color: BL,  border: `1px solid ${BL}40`,  borderRadius: 3, padding: "1px 4px" }}>FMP</span>}
            {stock.isDynamic && <span style={{ fontSize: 7, background: YL + "15", color: YL, border: `1px solid ${YL}30`, borderRadius: 3, padding: "1px 4px" }}>MOVER</span>}
            <span className="star" onClick={(e) => { e.stopPropagation(); onToggleWL(stock.symbol); }} style={{ fontSize: 11, color: isWatchlisted ? YL : TX2 }}>{isWatchlisted ? "★" : "☆"}</span>
          </div>
          <div style={{ fontSize: 8, color: TX2, marginBottom: 3 }}>{stock.sector}</div>
          <div style={{ fontSize: 11, color: TX }}>
            {Number.isFinite(stock.price) ? `$${stock.price.toFixed(2)}` : "N/A"}{" "}
            {Number.isFinite(stock.change)
              ? <span style={{ color: stock.change >= 0 ? GR : RD, fontWeight: 600 }}>{fmt(stock.change)}</span>
              : <span style={{ color: TX2, fontWeight: 600 }}>N/A</span>}
          </div>
          {stock.fiveDayReturn != null && <div style={{ fontSize: 8, color: TX2, marginTop: 1 }}>5d: <span style={{ color: stock.fiveDayReturn >= 0 ? GR : RD }}>{stock.fiveDayReturn >= 0 ? "+" : ""}{stock.fiveDayReturn.toFixed(1)}%</span></div>}
        </div>

        <div style={{ minWidth: 148 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor(stock.score), fontFamily: "'Space Grotesk',sans-serif", lineHeight: 1, minWidth: 34, display: "inline-block" }}>{displayScore}</span>
            <div>
              <div style={{ fontSize: 8, fontWeight: 700, color: scoreColor(stock.score), letterSpacing: 1 }}>{gradeLabel(stock)}</div>
              {/* Agreement between signals, not a probability. "(100/100)"
                  next to "Bullish" reads as a 100% chance of going up. */}
              <div style={{ fontSize: 8, color: biasColor(stock.tomorrowBias) }}>
                bias: {stock.technicalBias ?? stock.tomorrowBias}
              </div>
              {Number.isFinite(stock.biasConfidence) && (
                <div style={{ fontSize: 7, color: TX2 }}>signal agreement {stock.biasConfidence}/100</div>
              )}
              {/* Where this score sits among stocks of similar volatility. A high
                  score is far easier on a calm mega-cap than a violent microcap,
                  so the raw number alone invites comparing two different scales.
                  Descriptive only — it says nothing about what happens next. */}
              {stock.peerLabel && (
                <div style={{ fontSize: 7, color: BL }}>{stock.peerLabel}</div>
              )}
              {Number.isFinite(stock.dataCoverage) && stock.dataCoverage < 100 && (
                <div style={{ fontSize: 7, color: stock.dataCoverage >= 70 ? TX2 : YL }}>
                  data coverage {stock.dataCoverage}%
                </div>
              )}
            </div>
          </div>
          <SubBar label="MOMENTUM" value={stock.momentum} color={PU} />
          <SubBar label="POSITION" value={stock.positionScore ?? stock.volumeScore} color={BL} />
          <SubBar label="TREND"    value={stock.trend} color={GR} />
          <SubBar
            label="FUNDAMENTALS"
            value={stock.fundamentalsScore ?? stock.volumeScore}
            color={YL}
            // Only the top candidates are enriched each scan, to stay inside the
            // providers' quotas. Everything else genuinely has no fundamentals.
            evaluated={stock.hasFundamentals !== false}
          />
        </div>

        {rr && (
          <div style={{ background: CARD2, borderRadius: 7, padding: "8px 11px", minWidth: 118 }}>
            <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 4 }}>RISK / REWARD</div>
            <div style={{ fontSize: 8, color: TX2, marginBottom: 2 }}>Entry  <span style={{ color: TX }}>{Number.isFinite(rr.entry) ? `$${rr.entry.toFixed(2)}` : "N/A"}</span></div>
            {/* Stop distance is the number that exposes an untradeable setup. */}
            <div style={{ fontSize: 8, color: TX2, marginBottom: 2 }}>
              Stop   <span style={{ color: RD }}>{Number.isFinite(rr.stopLoss) ? `$${rr.stopLoss.toFixed(2)}` : "N/A"}</span>
              {Number.isFinite(rr.stopPct) && <span style={{ color: rr.stopPct > 12 ? RD : TX2 }}> (−{rr.stopPct.toFixed(1)}%)</span>}
            </div>
            <div style={{ fontSize: 8, color: TX2 }}>
              Target <span style={{ color: GR }}>{Number.isFinite(rr.target) ? `$${rr.target.toFixed(2)}` : "N/A"}</span>
              {Number.isFinite(rr.rr) && <span style={{ color: TX2 }}> · {rr.rr.toFixed(1)}R</span>}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 4, justifyContent: "center" }}>
          <a href={`https://finance.yahoo.com/quote/${stock.symbol}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ background: CARD2, border: `1px solid ${BD}`, borderRadius: 4, padding: "3px 7px", color: BL, fontSize: 8, textDecoration: "none" }}>Yahoo</a>
          <a href={`https://www.tradingview.com/chart/?symbol=${stock.symbol}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ background: CARD2, border: `1px solid ${BD}`, borderRadius: 4, padding: "3px 7px", color: GR, fontSize: 8, textDecoration: "none" }}>Chart</a>
        </div>
      </div>
    </div>
  );
}
